import { createAuthClient } from './auth-client.js';
import { renderAuthExperience } from './auth-ui.js';
import { createPhotoAttachmentController, renderPhotoSection } from './photo-attachments.js';
import companyDirectoryPreferences from './company-directory.cjs';
import routing from './routing.cjs';
import { renderAccessAdministration } from './admin-access.js';

const {
  APP_BASE_PATH,
  LOGIN_PATH,
  companyPath,
  equipmentTypePath,
  loginPath,
  legacyRedirectPath,
  parseAppRoute,
  safeReturnTo
} = routing;

const {
  moveCompanyDirectoryItem,
  normalizeCompanyDirectoryPreferences,
  toggleCompanyDirectoryVisibility
} = companyDirectoryPreferences;

const COMPANY_DIRECTORY_PREFERENCES_KEY = 'wb-parts.company-directory-preferences.v1';

(async function init() {
  const app = document.getElementById('app');

  const legacyPath = legacyRedirectPath(
    window.location.pathname,
    window.location.search,
    window.location.hash
  );
  if (legacyPath) {
    window.location.replace(legacyPath);
    return;
  }

  try {
    const authClient = await createAuthClient();
    const postLoginUrl = await authClient.completeSignInIfPresent();
    if (postLoginUrl) {
      window.location.replace(postLoginUrl);
      return;
    }

    const route = parseAppRoute(window.location.pathname, window.location.search);
    if (route.kind === 'admin') {
      await renderAccessAdministration(app, authClient);
      return;
    }
    const signedIn = await authClient.refreshSession();

    if (route.kind === 'callback') {
      window.location.replace(LOGIN_PATH);
      return;
    }

    if (route.kind === 'not-found') {
      renderNotFound(app);
      return;
    }

    if (route.kind === 'login') {
      if (signedIn) {
        const returnTo = getSafeReturnTo();
        if (returnTo) {
          window.location.replace(returnTo);
          return;
        }
        await renderCompanyDirectory(app, authClient);
        return;
      }
      renderSharedSignIn(app, authClient);
      return;
    }

    if (route.kind === 'base') {
      if (!signedIn) {
        window.location.replace(LOGIN_PATH);
        return;
      }
      await renderCompanyDirectory(app, authClient);
      return;
    }

    const requestedPath = route.kind === 'form'
      ? equipmentTypePath(route.slug, route.equipmentType)
      : companyPath(route.slug);

    if (!signedIn) {
      window.location.replace(loginPath(requestedPath));
      return;
    }

    const config = await loadConfig(route.slug, route.equipmentType);
    if (!config.ok || !config.company) {
      renderNotFound(app);
      return;
    }

    rememberCompanySlug(route.slug);
    await renderAuthorizedForm(app, config, config.company, config.equipmentType, authClient);
  } catch (error) {
    app.innerHTML = `
      <section class="notice error">
        <span aria-hidden="true">!</span>
        <div>
          <h1>Form could not load</h1>
          <p>${escapeHtml(error.message || 'Configuration failed to load.')}</p>
        </div>
      </section>
    `;
  }
})();

async function loadConfig(slug, equipmentType = '') {
  const query = new URLSearchParams({ companySlug: slug });
  if (equipmentType) {
    query.set('equipmentType', equipmentType);
  }
  const response = await fetch(`/api/form-config?${query}`, { cache: 'no-store' });
  if (!response.ok) {
    return { ok: false };
  }
  return response.json();
}

function renderTopbar(company, identity, showCompanyPicker = false, equipmentSwitch = null) {
  return renderPortalTopbar(
    identity,
    company.displayName,
    showCompanyPicker,
    equipmentSwitch ? {
      ...equipmentSwitch,
      path: equipmentTypePath(company.slug, equipmentSwitch.slug)
    } : null
  );
}

function renderPortalTopbar(
  identity,
  companyName = '',
  showCompanyPicker = false,
  equipmentSwitch = null
) {
  return `
    <header class="topbar">
      <div class="topbar-inner">
        <div class="portal-identity">
          <img class="portal-logo" src="/assets/branding/favicon.png" alt="W&amp;B Service Company">
          <h1>
            Parts Escalation
            ${companyName ? `<span aria-hidden="true">/</span> <strong>${escapeHtml(companyName)}</strong>` : ''}
          </h1>
        </div>
        ${identity ? `
          <div class="portal-account">
            <button class="account-email" type="button" title="Signed-in account">${escapeHtml(identity.email)}</button>
            <button class="account-menu-toggle" data-account-menu-toggle type="button" aria-label="Account options" aria-expanded="false" aria-controls="account-menu">
              <i></i><i></i><i></i>
            </button>
            <div id="account-menu" class="portal-account-menu" data-account-menu hidden>
              ${equipmentSwitch ? `
                <a href="${escapeHtml(equipmentSwitch.path)}">Switch to ${escapeHtml(equipmentSwitch.displayName)}</a>
              ` : ''}
              ${showCompanyPicker ? `
                <a href="${APP_BASE_PATH}">Switch company</a>
                <div class="account-menu-divider" role="separator"></div>
              ` : ''}
              <a href="mailto:support@wbservice.com?subject=Support%20Request%20%E2%80%93%20W%26B%20Parts%20Escalation%20Portal">Contact us</a>
              <button id="sign-out-button" data-sign-out type="button">Sign out</button>
              <p class="account-menu-version">Version 0.4.3</p>
            </div>
          </div>
        ` : ''}
      </div>
    </header>
  `;
}

function rememberCompanySlug(slug) {
  window.localStorage.setItem('lastCompanySlug', slug);
}

function getSafeReturnTo() {
  const value = new URL(window.location.href).searchParams.get('returnTo') || '';
  return safeReturnTo(value, window.location.origin);
}

function renderSharedSignIn(app, authClient) {
  const returnTo = getSafeReturnTo();
  renderAuthExperience(app, {
    authClient,
    onSignedIn: async () => window.location.replace(returnTo || APP_BASE_PATH)
  });
}

async function renderCompanyDirectory(app, authClient) {
  const accessToken = await authClient.getAccessToken();
  if (!accessToken) {
    window.location.replace(LOGIN_PATH);
    return;
  }

  let response;
  let result;
  try {
    response = await fetch('/api/companies', {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-WB-Parts-Access-Token': `Bearer ${accessToken}`
      }
    });
    result = await response.json().catch(() => ({}));
  } catch {
    renderDirectoryUnavailable(app, authClient);
    return;
  }

  if (response.status === 401) {
    await authClient.clearSession();
    window.location.replace(LOGIN_PATH);
    return;
  }

  if (!response.ok || !result.ok || !Array.isArray(result.companies)) {
    renderDirectoryUnavailable(app, authClient, result.message);
    return;
  }

  if (result.companies.length === 0) {
    renderNoCompanyAccess(app, authClient);
    return;
  }

  if (result.companies.length === 1) {
    window.location.replace(companyPath(result.companies[0].slug));
    return;
  }

  renderCompanyChooser(app, result.companies, authClient);
}

function renderCompanyChooser(app, companies, authClient) {
  const state = {
    authClient,
    companies,
    editMode: false,
    preferences: loadCompanyDirectoryPreferences(companies)
  };
  renderCompanyChooserView(app, state);
}

function renderCompanyChooserView(app, state, focus = null) {
  const companyBySlug = new Map(state.companies.map((company) => [company.slug, company]));
  const orderedCompanies = state.preferences.order
    .map((slug) => companyBySlug.get(slug))
    .filter(Boolean);
  const hiddenSlugs = new Set(state.preferences.hidden);
  const displayedCompanies = state.editMode
    ? orderedCompanies
    : orderedCompanies.filter((company) => !hiddenSlugs.has(company.slug));
  const visibleCount = orderedCompanies.length - hiddenSlugs.size;

  app.innerHTML = `
    ${renderPortalTopbar(state.authClient.getIdentity())}
    <section class="company-directory" aria-labelledby="company-directory-title">
      <h2 id="company-directory-title">Choose a company form</h2>
      <div class="company-list${state.editMode ? ' company-edit-list' : ''}"${state.editMode ? ' aria-label="Edit company order and visibility"' : ''}>
        ${state.editMode ? displayedCompanies.map((company) => {
          const isHidden = hiddenSlugs.has(company.slug);
          const cannotHide = !isHidden && visibleCount <= 1;
          return `
            <div class="company-edit-row${isHidden ? ' is-hidden' : ''}" data-company-row="${escapeHtml(company.slug)}">
              <button class="company-drag-handle" data-company-drag type="button" draggable="true" aria-label="Move ${escapeHtml(company.displayName)}. Drag or use the up and down arrow keys.">
                ${dragHandleIcon()}
              </button>
              <span class="company-edit-name">${escapeHtml(company.displayName)}</span>
              <button class="company-visibility-button" data-company-visibility type="button"
                aria-label="${isHidden ? 'Show' : 'Hide'} ${escapeHtml(company.displayName)}"
                aria-pressed="${isHidden}"${cannotHide ? ' disabled title="At least one company must remain visible"' : ''}>
                ${companyVisibilityIcon(isHidden)}
              </button>
            </div>
          `;
        }).join('') : displayedCompanies.map((company) => `
          <a class="company-link" href="${escapeHtml(companyPath(company.slug))}">
            <span>${escapeHtml(company.displayName)}</span>
            <span aria-hidden="true">&rarr;</span>
          </a>
        `).join('')}
      </div>
      <button class="company-directory-edit-button" data-company-edit type="button" aria-expanded="${state.editMode}">
        ${state.editMode ? 'Done' : 'Show/hide companies'}
      </button>
    </section>
  `;
  bindSignOut(app, state.authClient);
  bindCompanyDirectoryEditor(app, state);

  if (focus) {
    const selector = focus.control === 'edit'
      ? '[data-company-edit]'
      : focus.control === 'visibility'
        ? `[data-company-row="${cssEscape(focus.slug)}"] [data-company-visibility]`
        : `[data-company-row="${cssEscape(focus.slug)}"] [data-company-drag]`;
    app.querySelector(selector)?.focus();
  }
}

function bindCompanyDirectoryEditor(app, state) {
  const editButton = app.querySelector('[data-company-edit]');
  editButton?.addEventListener('click', () => {
    state.editMode = !state.editMode;
    renderCompanyChooserView(app, state, { control: 'edit' });
  });

  if (!state.editMode) return;
  const list = app.querySelector('.company-edit-list');
  if (!list) return;
  let nativeDragSlug = '';

  list.addEventListener('dragover', (event) => {
    if (!nativeDragSlug) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    const target = event.target.closest('[data-company-row]');
    if (!target || target.dataset.companyRow === nativeDragSlug) return;
    const bounds = target.getBoundingClientRect();
    state.preferences = moveCompanyDirectoryItem(
      state.preferences,
      nativeDragSlug,
      target.dataset.companyRow || '',
      event.clientY > bounds.top + bounds.height / 2
    );
    applyCompanyDirectoryOrder(list, state.preferences.order);
  });

  list.addEventListener('drop', (event) => {
    if (nativeDragSlug) event.preventDefault();
  });

  for (const visibilityButton of list.querySelectorAll('[data-company-visibility]')) {
    visibilityButton.addEventListener('click', () => {
      const row = visibilityButton.closest('[data-company-row]');
      const slug = row?.dataset.companyRow || '';
      state.preferences = toggleCompanyDirectoryVisibility(state.preferences, slug);
      saveCompanyDirectoryPreferences(state.preferences);
      renderCompanyChooserView(app, state, { control: 'visibility', slug });
    });
  }

  for (const handle of list.querySelectorAll('[data-company-drag]')) {
    const row = handle.closest('[data-company-row]');
    const slug = row?.dataset.companyRow || '';
    let pointerId = null;

    handle.addEventListener('dragstart', (event) => {
      nativeDragSlug = slug;
      row?.classList.add('is-dragging');
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', slug);
      }
    });

    handle.addEventListener('dragend', () => {
      nativeDragSlug = '';
      row?.classList.remove('is-dragging');
      saveCompanyDirectoryPreferences(state.preferences);
    });

    handle.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      const index = state.preferences.order.indexOf(slug);
      const targetIndex = event.key === 'ArrowUp' ? index - 1 : index + 1;
      const targetSlug = state.preferences.order[targetIndex];
      if (!targetSlug) return;
      event.preventDefault();
      state.preferences = moveCompanyDirectoryItem(
        state.preferences,
        slug,
        targetSlug,
        event.key === 'ArrowDown'
      );
      saveCompanyDirectoryPreferences(state.preferences);
      renderCompanyChooserView(app, state, { control: 'drag', slug });
    });

    handle.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.pointerType === 'mouse') return;
      pointerId = event.pointerId;
      handle.setPointerCapture(pointerId);
      row?.classList.add('is-dragging');
    });

    handle.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId || !row) return;
      event.preventDefault();
      const target = Array.from(list.querySelectorAll('[data-company-row]')).find((candidate) => {
        if (candidate === row) return false;
        const bounds = candidate.getBoundingClientRect();
        return event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      });
      if (!target) return;
      const bounds = target.getBoundingClientRect();
      state.preferences = moveCompanyDirectoryItem(
        state.preferences,
        slug,
        target.dataset.companyRow || '',
        event.clientY > bounds.top + bounds.height / 2
      );
      applyCompanyDirectoryOrder(list, state.preferences.order);
    });

    const finishDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      row?.classList.remove('is-dragging');
      saveCompanyDirectoryPreferences(state.preferences);
    };
    handle.addEventListener('pointerup', finishDrag);
    handle.addEventListener('pointercancel', finishDrag);
  }
}

function loadCompanyDirectoryPreferences(companies) {
  let storedPreferences = {};
  try {
    storedPreferences = JSON.parse(window.localStorage.getItem(COMPANY_DIRECTORY_PREFERENCES_KEY) || '{}');
  } catch {
    storedPreferences = {};
  }
  return normalizeCompanyDirectoryPreferences(
    companies.map((company) => company.slug),
    storedPreferences
  );
}

function saveCompanyDirectoryPreferences(preferences) {
  try {
    window.localStorage.setItem(COMPANY_DIRECTORY_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    // The chooser still works for this page load when browser storage is unavailable.
  }
}

function applyCompanyDirectoryOrder(list, order) {
  const rows = new Map(Array.from(list.querySelectorAll('[data-company-row]'))
    .map((row) => [row.dataset.companyRow, row]));
  for (const slug of order) {
    const row = rows.get(slug);
    if (row) list.append(row);
  }
}

function dragHandleIcon() {
  return `
    <svg viewBox="0 0 18 24" aria-hidden="true" focusable="false">
      <circle cx="6" cy="5" r="1.5"></circle><circle cx="12" cy="5" r="1.5"></circle>
      <circle cx="6" cy="12" r="1.5"></circle><circle cx="12" cy="12" r="1.5"></circle>
      <circle cx="6" cy="19" r="1.5"></circle><circle cx="12" cy="19" r="1.5"></circle>
    </svg>
  `;
}

function companyVisibilityIcon(isHidden) {
  if (isHidden) {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"></path>
        <circle cx="12" cy="12" r="2.8"></circle>
      </svg>
    `;
  }
  return `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 3l18 18"></path>
      <path d="M10.6 6.1A10.9 10.9 0 0 1 12 6c6 0 9.5 6 9.5 6a16.2 16.2 0 0 1-2.1 2.8M6.2 6.2C3.8 8 2.5 12 2.5 12s3.5 6 9.5 6c1.5 0 2.8-.4 4-1"></path>
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"></path>
    </svg>
  `;
}

function cssEscape(value) {
  return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function renderNoCompanyAccess(app, authClient) {
  app.innerHTML = `
    ${renderPortalTopbar(authClient.getIdentity())}
    <section class="access-page" aria-labelledby="access-denied-title">
      <h2 id="access-denied-title">Access not approved</h2>
      <p>This account has not been approved for a W&amp;B Parts Escalation form.</p>
      <p>Please contact us at <a class="access-support-email" href="mailto:support@wbservice.com?subject=Support%20Request%20%E2%80%93%20W%26B%20Parts%20Escalation%20Portal">support@wbservice.com</a>.</p>
      <button class="secondary-button access-action" data-sign-out type="button">Sign out</button>
    </section>
  `;
  bindSignOut(app, authClient);
}

function renderDirectoryUnavailable(app, authClient, message) {
  app.innerHTML = `
    ${renderPortalTopbar(authClient.getIdentity())}
    <section class="notice error access-notice" aria-labelledby="access-unavailable-title">
      <span aria-hidden="true">!</span>
      <div>
        <h1 id="access-unavailable-title">Form access could not be verified</h1>
        <p>${escapeHtml(message || 'Please refresh the page and try again.')}</p>
      </div>
    </section>
  `;
  bindSignOut(app, authClient);
}

async function renderAuthorizedForm(app, config, company, equipmentType, authClient) {
  const accessToken = await authClient.getAccessToken();
  if (!accessToken) {
    const returnTo = equipmentType
      ? equipmentTypePath(company.slug, equipmentType.slug)
      : companyPath(company.slug);
    window.location.replace(loginPath(returnTo));
    return;
  }

  const multipleCompaniesPromise = hasMultipleAccessibleCompanies(accessToken);

  let response;
  let result;
  try {
    response = await fetch(`/api/form-access?companySlug=${encodeURIComponent(company.slug)}`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-WB-Parts-Access-Token': `Bearer ${accessToken}`
      }
    });
    result = await response.json().catch(() => ({}));
  } catch {
    renderAccessUnavailable(
      app,
      config,
      company,
      authClient,
      '',
      await multipleCompaniesPromise
    );
    return;
  }

  if (response.status === 401) {
    await authClient.clearSession();
    const returnTo = equipmentType
      ? equipmentTypePath(company.slug, equipmentType.slug)
      : companyPath(company.slug);
    window.location.replace(loginPath(returnTo));
    return;
  }

  const showCompanyPicker = await multipleCompaniesPromise;

  if (response.status === 403) {
    renderAccessDenied(app, config, company, authClient, showCompanyPicker);
    return;
  }

  if (!response.ok || !result.ok || !result.authorized) {
    renderAccessUnavailable(app, config, company, authClient, result.message, showCompanyPicker);
    return;
  }

  if (!equipmentType) {
    renderEquipmentTypeChooser(app, config, company, authClient, showCompanyPicker);
    return;
  }

  renderForm(app, config, company, equipmentType, authClient, showCompanyPicker);
}

function renderEquipmentTypeChooser(app, config, company, authClient, showCompanyPicker) {
  app.innerHTML = `
    ${renderTopbar(company, authClient.getIdentity(), showCompanyPicker)}
    <section class="company-directory equipment-directory" aria-label="Equipment type">
      <div class="company-list equipment-list">
        ${config.equipmentTypes.map((equipmentType) => `
          <a class="company-link equipment-link" href="${escapeHtml(equipmentTypePath(company.slug, equipmentType.slug))}">
            <span>${escapeHtml(equipmentType.displayName)}</span>
            <span aria-hidden="true">&rarr;</span>
          </a>
        `).join('')}
      </div>
    </section>
  `;
  bindSignOut(app, authClient);
}

async function hasMultipleAccessibleCompanies(accessToken) {
  try {
    const response = await fetch('/api/companies', {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-WB-Parts-Access-Token': `Bearer ${accessToken}`
      }
    });
    const result = await response.json().catch(() => ({}));
    return response.ok && result.ok && Array.isArray(result.companies) && result.companies.length > 1;
  } catch {
    return false;
  }
}

function renderAccessDenied(app, config, company, authClient, showCompanyPicker) {
  const identity = authClient.getIdentity();
  const domain = getEmailDomain(identity && identity.email);
  app.innerHTML = `
    ${renderTopbar(company, identity, showCompanyPicker)}
    <section class="access-page" aria-labelledby="access-denied-title">
      <h2 id="access-denied-title">Access not approved</h2>
      <p>The email domain <strong>${escapeHtml(domain || 'for this account')}</strong> has not been approved for the ${escapeHtml(company.displayName)} form.</p>
      <p>Please contact us at <a class="access-support-email" href="mailto:support@wbservice.com?subject=Support%20Request%20%E2%80%93%20W%26B%20Parts%20Escalation%20Portal">support@wbservice.com</a>.</p>
      <button class="secondary-button access-action" data-sign-out type="button">Sign out</button>
    </section>
  `;
  bindSignOut(app, authClient);
}

function renderAccessUnavailable(app, config, company, authClient, message, showCompanyPicker) {
  const identity = authClient.getIdentity();
  app.innerHTML = `
    ${renderTopbar(company, identity, showCompanyPicker)}
    <section class="notice error access-notice" aria-labelledby="access-unavailable-title">
      <span aria-hidden="true">!</span>
      <div>
        <h1 id="access-unavailable-title">Form access could not be verified</h1>
        <p>${escapeHtml(message || 'Please refresh the page and try again.')}</p>
      </div>
    </section>
  `;
  bindSignOut(app, authClient);
}

function renderNotFound(app) {
  app.innerHTML = `
    <section class="notice error">
      <span aria-hidden="true">!</span>
      <div>
        <h1>Escalation form not found</h1>
        <p>Check that you are using a valid company and equipment form link.</p>
      </div>
    </section>
  `;
}

function renderForm(app, config, company, equipmentType, authClient, showCompanyPicker) {
  const values = getInitialValues(config.fields);
  const groupedFields = groupFieldsBySection(config.fields);
  const identity = authClient.getIdentity();
  const equipmentSwitch = config.equipmentTypes.find((type) => type.slug !== equipmentType.slug);
  let submissionInProgress = false;

  app.innerHTML = `
    ${renderTopbar(company, identity, showCompanyPicker, equipmentSwitch)}

    <section class="form-shell">
      <header class="form-heading">
        <h2>${escapeHtml(equipmentType.displayName)}</h2>
      </header>
      <form id="order-form" class="order-form" novalidate>
        ${Object.entries(groupedFields).map(([section, sectionFields]) => `
          <fieldset class="form-section">
            <legend>${escapeHtml(section)}</legend>
            <div class="field-grid${section === 'Part Details' ? ' part-details-grid' : ''}">
              ${section === 'Notes' ? renderPhotoSection() : ''}
              ${section === 'Part Details'
                ? renderPartDetails(sectionFields, values)
                : sectionFields.map((field) => renderField(field, values[field.key])).join('')}
            </div>
          </fieldset>
        `).join('')}

        <div id="status-panel" class="form-status hidden" role="status" aria-live="polite"></div>

        <div class="submit-row">
          <button id="clear-button" class="secondary-button" type="button">Clear Form</button>
          <button id="submit-button" class="submit-button" type="submit">Submit</button>
        </div>
      </form>
    </section>
  `;

  const form = document.getElementById('order-form');
  const clearButton = document.getElementById('clear-button');
  form.dataset.companySlug = company.slug;
  const photoAttachments = createPhotoAttachmentController(form, {
    getAccessToken: () => authClient.getAccessToken()
  });
  bindDatePickers(app);
  const partsEditor = createPartsEditor(app);
  bindSignOut(app, authClient);

  form.addEventListener('input', (event) => {
    const target = event.target;
    if (target && target.name) {
      values[target.name] = target.value;
      clearFieldError(target.name);
    }
  });

  clearButton.addEventListener('click', () => {
    for (const field of config.fields) {
      values[field.key] = '';
      const input = form.elements[field.key];
      if (input) {
        input.value = '';
      }
      const dateDisplay = document.getElementById(`${field.key}-display`);
      if (dateDisplay) {
        dateDisplay.value = '';
      }
      clearFieldError(field.key);
    }
    partsEditor.clear();
    photoAttachments.clear();
    setStatus('');
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (submissionInProgress) {
      return;
    }
    const parts = partsEditor.getParts();
    const validation = validateForm(config, company.slug, equipmentType.slug, values, parts);

    clearAllFieldErrors(config.fields);
    if (!validation.valid) {
      showFieldErrors(validation.errors);
      setStatus('Please review the highlighted fields.', 'error');
      return;
    }

    submissionInProgress = true;
    const submitted = await submitOrder(
      app,
      config,
      company,
      equipmentType,
      values,
      parts,
      authClient,
      showCompanyPicker,
      photoAttachments
    );
    if (!submitted) {
      submissionInProgress = false;
    }
  });
}

function renderField(field, value) {
  if (field.type === 'date') {
    return renderDateField(field, value);
  }
  const requiredMark = field.required ? '<em>*</em>' : '';
  const common = `
    name="${escapeHtml(field.key)}"
    id="${escapeHtml(field.key)}"
    ${field.required ? 'required' : ''}
    ${field.autocomplete ? `autocomplete="${escapeHtml(field.autocomplete)}"` : ''}
    ${field.min ? `min="${field.min}"` : ''}
    aria-describedby="${escapeHtml(field.key)}-error"
  `;

  const input = field.type === 'textarea'
    ? `<textarea ${common} rows="${field.rows || 3}">${escapeHtml(value || '')}</textarea>`
    : `<input ${common} type="${escapeHtml(field.type)}" value="${escapeHtml(value || '')}">`;

  return `
    <label class="field field-${escapeHtml(field.type)}" for="${escapeHtml(field.key)}">
      <span>${escapeHtml(field.label)}${requiredMark}</span>
      ${input}
      <small id="${escapeHtml(field.key)}-error" class="field-error"></small>
    </label>
  `;
}

function renderPartDetails(fields, values) {
  const partNumberField = fields.find((field) => field.key === 'partNumber');
  const partQuantityField = fields.find((field) => field.key === 'partQuantity');
  const sideFields = fields.filter((field) => !['partNumber', 'partQuantity'].includes(field.key));

  if (!partNumberField || !partQuantityField) {
    return fields.map((field) => renderField(field, values[field.key])).join('');
  }

  const partRequiredMark = partNumberField.required ? '<em>*</em>' : '';

  return `
    <div class="parts-editor" data-parts-editor data-part-number-required="${partNumberField.required}">
      <div class="part-row-labels" aria-hidden="true">
        <span>${escapeHtml(partNumberField.label)}${partRequiredMark}</span>
        <span>Quantity</span>
        <span></span>
      </div>
      <div class="part-rows" data-part-rows>
        ${renderPartRow(0, { partNumber: '', quantity: '1' }, partNumberField.required)}
      </div>
      <button class="add-part-button" data-add-part type="button">+ Add another part</button>
      <small id="partNumber-error" class="field-error"></small>
      <small id="partQuantity-error" class="field-error"></small>
    </div>
    <div class="part-details-side">
      ${sideFields.map((field) => renderField(field, values[field.key])).join('')}
    </div>
  `;
}

function renderPartRow(index, part, partNumberRequired) {
  const rowNumber = index + 1;
  return `
    <div class="part-entry-row" data-part-row>
      <input
        data-part-number
        type="text"
        value="${escapeHtml(part.partNumber)}"
        autocomplete="off"
        spellcheck="false"
        aria-label="Part number ${rowNumber}"
        aria-required="${partNumberRequired}"
        aria-describedby="partNumber-error"
      >
      <input
        data-part-quantity
        type="number"
        value="${escapeHtml(part.quantity)}"
        min="1"
        step="1"
        inputmode="numeric"
        aria-label="Quantity for part ${rowNumber}"
        aria-required="true"
        aria-describedby="partQuantity-error"
      >
      ${index === 0
        ? '<span class="part-remove-placeholder" aria-hidden="true"></span>'
        : `<button class="remove-part-button" data-remove-part="${index}" type="button" aria-label="Remove part ${rowNumber}">&times;</button>`}
    </div>
  `;
}

function createPartsEditor(app) {
  const editor = app.querySelector('[data-parts-editor]');
  const rowsContainer = editor.querySelector('[data-part-rows]');
  const partNumberRequired = editor.dataset.partNumberRequired === 'true';
  let parts = [{ partNumber: '', quantity: '1' }];

  function readParts() {
    parts = [...rowsContainer.querySelectorAll('[data-part-row]')].map((row) => ({
      partNumber: row.querySelector('[data-part-number]').value.trim(),
      quantity: row.querySelector('[data-part-quantity]').value.trim()
    }));
    return parts;
  }

  function renderRows(focusIndex = null) {
    rowsContainer.innerHTML = parts
      .map((part, index) => renderPartRow(index, part, partNumberRequired))
      .join('');
    if (focusIndex !== null) {
      rowsContainer.querySelectorAll('[data-part-number]')[focusIndex]?.focus();
    }
  }

  editor.addEventListener('input', (event) => {
    if (event.target.matches('[data-part-number]')) clearFieldError('partNumber');
    if (event.target.matches('[data-part-quantity]')) clearFieldError('partQuantity');
  });

  editor.querySelector('[data-add-part]').addEventListener('click', () => {
    readParts();
    parts.push({ partNumber: '', quantity: '1' });
    renderRows(parts.length - 1);
  });

  rowsContainer.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-remove-part]');
    if (!removeButton) return;
    readParts();
    const index = Number(removeButton.dataset.removePart);
    if (!Number.isInteger(index) || index <= 0 || index >= parts.length) return;
    parts.splice(index, 1);
    renderRows(Math.min(index, parts.length - 1));
    clearFieldError('partNumber');
    clearFieldError('partQuantity');
  });

  return {
    clear() {
      parts = [{ partNumber: '', quantity: '1' }];
      renderRows();
      clearFieldError('partNumber');
      clearFieldError('partQuantity');
    },
    getParts: readParts
  };
}

function renderDateField(field, value) {
  const requiredMark = field.required ? '<em>*</em>' : '';
  const key = escapeHtml(field.key);
  const normalizedValue = /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? String(value) : '';
  const displayValue = normalizedValue ? formatDateForDisplay(parseIsoDate(normalizedValue)) : '';

  return `
    <div class="field field-date">
      <label for="${key}-display"><span>${escapeHtml(field.label)}${requiredMark}</span></label>
      <div class="custom-date-control" data-date-picker="${key}">
        <input
          id="${key}-display"
          data-date-display-for="${key}"
          type="text"
          value="${escapeHtml(displayValue)}"
          placeholder="mm/dd/yyyy"
          inputmode="numeric"
          maxlength="10"
          autocomplete="off"
          aria-haspopup="dialog"
          aria-expanded="false"
          aria-describedby="${key}-error"
        >
        <button class="calendar-trigger" type="button" aria-label="Choose ${escapeHtml(field.label)}" tabindex="-1"><span aria-hidden="true"></span></button>
        <input id="${key}" name="${key}" type="hidden" value="${escapeHtml(normalizedValue)}">
      </div>
      <small id="${key}-error" class="field-error"></small>
    </div>
  `;
}

const DATE_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DATE_WEEKDAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
let datePickerAbortController = null;

function padDatePart(value) {
  return String(value).padStart(2, '0');
}

function formatDateForDisplay(date) {
  return `${padDatePart(date.getMonth() + 1)}/${padDatePart(date.getDate())}/${date.getFullYear()}`;
}

function formatDateForValue(date) {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

function parseIsoDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseDisplayDate(value) {
  const match = String(value || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function maskDateInput(input, inputEvent) {
  const originalValue = input.value;
  const originalCursor = input.selectionStart ?? originalValue.length;
  const digitsBeforeCursor = originalValue.slice(0, originalCursor).replace(/\D/g, '').length;
  const digits = originalValue.replace(/\D/g, '').slice(0, 8);
  const deleting = String(inputEvent.inputType || '').startsWith('delete');

  let formatted = digits.slice(0, 2);
  if (digits.length > 2 || (digits.length === 2 && !deleting)) formatted += '/';
  formatted += digits.slice(2, 4);
  if (digits.length > 4 || (digits.length === 4 && !deleting)) formatted += '/';
  formatted += digits.slice(4, 8);
  input.value = formatted;

  let cursor = 0;
  let digitsSeen = 0;
  while (cursor < formatted.length && digitsSeen < digitsBeforeCursor) {
    if (/\d/.test(formatted[cursor])) digitsSeen += 1;
    cursor += 1;
  }
  if (!deleting && originalCursor === originalValue.length) cursor = formatted.length;
  input.setSelectionRange(cursor, cursor);
}

function bindDatePickers(app) {
  if (datePickerAbortController) datePickerAbortController.abort();
  datePickerAbortController = new AbortController();
  const { signal } = datePickerAbortController;
  const datePickers = [];

  function closeDatePickers(except) {
    for (const picker of datePickers) {
      if (picker === except) continue;
      picker.control.classList.remove('open', 'open-upward', 'viewport-overlay');
      picker.popover.hidden = true;
      picker.displayInput.setAttribute('aria-expanded', 'false');
      picker.monthMenu.hidden = true;
      picker.monthToggle.setAttribute('aria-expanded', 'false');
      picker.yearMenu.hidden = true;
      picker.yearToggle.setAttribute('aria-expanded', 'false');
    }
  }

  function renderCalendar(picker) {
    const viewYear = picker.viewDate.getFullYear();
    const viewMonth = picker.viewDate.getMonth();
    picker.monthToggle.dataset.month = String(viewMonth);
    picker.monthToggle.querySelector('span').textContent = DATE_MONTH_NAMES[viewMonth];
    picker.monthToggle.setAttribute('aria-expanded', 'false');
    picker.monthMenu.hidden = true;
    picker.monthMenu.innerHTML = Array.from(
      { length: 12 },
      (_, index) => new Date(viewYear, viewMonth - 1 + index, 1)
    ).map((date) => `
      <button
        class="month-option"
        type="button"
        role="option"
        data-month="${date.getMonth()}"
        data-year="${date.getFullYear()}"
      >${DATE_MONTH_NAMES[date.getMonth()]}</button>
    `).join('');
    picker.monthMenu.querySelectorAll('.month-option').forEach((option) => {
      const selected = Number(option.dataset.month) === viewMonth &&
        Number(option.dataset.year) === viewYear;
      option.classList.toggle('selected', selected);
      option.setAttribute('aria-selected', String(selected));
    });

    picker.yearToggle.dataset.year = String(viewYear);
    picker.yearToggle.querySelector('span').textContent = String(viewYear);
    picker.yearToggle.setAttribute('aria-expanded', 'false');
    picker.yearMenu.hidden = true;
    picker.yearMenu.querySelectorAll('.year-option').forEach((option) => {
      const selected = Number(option.dataset.year) === viewYear;
      option.classList.toggle('selected', selected);
      option.setAttribute('aria-selected', String(selected));
    });

    picker.grid.innerHTML = '';
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const gridStart = new Date(viewYear, viewMonth, 1 - firstOfMonth.getDay());

    for (let index = 0; index < 42; index += 1) {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'calendar-day';
      button.textContent = date.getDate();
      button.setAttribute('role', 'gridcell');
      button.setAttribute(
        'aria-label',
        `${DATE_MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
      );
      if (date.getMonth() !== viewMonth) button.classList.add('other-month');
      if (picker.selectedDate && formatDateForValue(picker.selectedDate) === formatDateForValue(date)) {
        button.classList.add('selected');
        button.setAttribute('aria-selected', 'true');
      }
      button.addEventListener('click', () => {
        picker.selectedDate = date;
        picker.viewDate = new Date(date.getFullYear(), date.getMonth(), 1);
        picker.displayInput.value = formatDateForDisplay(date);
        picker.hiddenInput.value = formatDateForValue(date);
        picker.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
        closeDatePickers();
        picker.displayInput.focus();
      });
      picker.grid.appendChild(button);
    }
  }

  function openDatePicker(picker) {
    closeDatePickers(picker);
    picker.control.classList.add('open', 'open-upward');
    picker.popover.hidden = false;
    picker.displayInput.setAttribute('aria-expanded', 'true');
    let autofilledToday = false;

    if (!picker.displayInput.value.trim()) {
      const today = new Date();
      picker.selectedDate = today;
      picker.viewDate = new Date(today.getFullYear(), today.getMonth(), 1);
      picker.displayInput.value = formatDateForDisplay(today);
      picker.hiddenInput.value = formatDateForValue(today);
      picker.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
      autofilledToday = true;
    }

    renderCalendar(picker);
    const roomAbove = picker.control.getBoundingClientRect().top - 12;
    if (roomAbove < picker.popover.offsetHeight) {
      picker.control.classList.remove('open-upward');
      picker.control.classList.add('viewport-overlay');
    }
    if (autofilledToday) picker.displayInput.select();
  }

  app.querySelectorAll('[data-date-picker]').forEach((control) => {
    const displayInput = control.querySelector('[data-date-display-for]');
    const hiddenInput = document.getElementById(control.dataset.datePicker);
    const fieldLabel = control.closest('.field').querySelector('label').textContent.trim();
    const popover = document.createElement('div');
    popover.className = 'date-popover';
    popover.hidden = true;
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-label', `Choose ${fieldLabel}`);
    popover.innerHTML = `
      <div class="calendar-header">
        <button class="month-nav previous-month" type="button" aria-label="Previous month">&#8249;</button>
        <div class="month-year-controls">
          <div class="month-picker">
            <button class="month-toggle" type="button" aria-label="Choose month" aria-expanded="false"><span></span><i aria-hidden="true"></i></button>
            <div class="month-menu" role="listbox" aria-label="Month" hidden></div>
          </div>
          <div class="year-picker">
            <button class="year-toggle" type="button" aria-label="Choose year" aria-expanded="false"><span></span><i aria-hidden="true"></i></button>
            <div class="year-menu" role="listbox" aria-label="Year" hidden></div>
          </div>
        </div>
        <button class="month-nav next-month" type="button" aria-label="Next month">&#8250;</button>
      </div>
      <div class="weekday-row" aria-hidden="true">${DATE_WEEKDAY_NAMES.map((day) => `<span>${day}</span>`).join('')}</div>
      <div class="calendar-grid" role="grid"></div>
    `;
    control.appendChild(popover);

    const monthToggle = popover.querySelector('.month-toggle');
    const monthMenu = popover.querySelector('.month-menu');
    const yearToggle = popover.querySelector('.year-toggle');
    const yearMenu = popover.querySelector('.year-menu');
    const today = new Date();
    const initialDate = parseIsoDate(hiddenInput.value);
    yearMenu.innerHTML = Array.from({ length: 12 }, (_, index) => today.getFullYear() - 1 + index)
      .map((year) => `<button class="year-option" type="button" role="option" data-year="${year}">${year}</button>`)
      .join('');

    const picker = {
      control,
      displayInput,
      hiddenInput,
      popover,
      monthToggle,
      monthMenu,
      yearToggle,
      yearMenu,
      grid: popover.querySelector('.calendar-grid'),
      viewDate: initialDate
        ? new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
        : new Date(today.getFullYear(), today.getMonth(), 1),
      selectedDate: initialDate
    };
    datePickers.push(picker);

    control.addEventListener('click', (event) => {
      if (event.target.closest('.date-popover')) return;
      openDatePicker(picker);
    }, { signal });
    displayInput.addEventListener('input', (event) => {
      maskDateInput(displayInput, event);
      const typedDate = parseDisplayDate(displayInput.value);
      picker.hiddenInput.value = typedDate
        ? formatDateForValue(typedDate)
        : displayInput.value.trim();
      picker.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
      if (!typedDate) return;
      picker.selectedDate = typedDate;
      picker.viewDate = new Date(typedDate.getFullYear(), typedDate.getMonth(), 1);
      if (!picker.popover.hidden) renderCalendar(picker);
    }, { signal });
    displayInput.addEventListener('blur', () => {
      const typedDate = parseDisplayDate(displayInput.value);
      if (typedDate) displayInput.value = formatDateForDisplay(typedDate);
    }, { signal });
    displayInput.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openDatePicker(picker);
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const typedDate = parseDisplayDate(displayInput.value);
        if (typedDate) {
          displayInput.value = formatDateForDisplay(typedDate);
          closeDatePickers();
        } else {
          openDatePicker(picker);
        }
      }
    }, { signal });
    popover.addEventListener('click', (event) => event.stopPropagation(), { signal });
    popover.querySelector('.previous-month').addEventListener('click', () => {
      picker.viewDate = new Date(picker.viewDate.getFullYear(), picker.viewDate.getMonth() - 1, 1);
      renderCalendar(picker);
    }, { signal });
    popover.querySelector('.next-month').addEventListener('click', () => {
      picker.viewDate = new Date(picker.viewDate.getFullYear(), picker.viewDate.getMonth() + 1, 1);
      renderCalendar(picker);
    }, { signal });
    monthToggle.addEventListener('click', () => {
      const willOpen = monthMenu.hidden;
      yearMenu.hidden = true;
      yearToggle.setAttribute('aria-expanded', 'false');
      monthMenu.hidden = !willOpen;
      monthToggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) monthMenu.querySelector('.month-option.selected')?.scrollIntoView({ block: 'nearest' });
    }, { signal });
    monthMenu.addEventListener('click', (event) => {
      const option = event.target.closest('.month-option');
      if (!option) return;
      picker.viewDate = new Date(Number(option.dataset.year), Number(option.dataset.month), 1);
      renderCalendar(picker);
    }, { signal });
    yearToggle.addEventListener('click', () => {
      const willOpen = yearMenu.hidden;
      monthMenu.hidden = true;
      monthToggle.setAttribute('aria-expanded', 'false');
      yearMenu.hidden = !willOpen;
      yearToggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) yearMenu.querySelector('.year-option.selected')?.scrollIntoView({ block: 'nearest' });
    }, { signal });
    yearMenu.querySelectorAll('.year-option').forEach((option) => {
      option.addEventListener('click', () => {
        picker.viewDate = new Date(Number(option.dataset.year), Number(monthToggle.dataset.month), 1);
        renderCalendar(picker);
      }, { signal });
    });
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-date-picker]')) closeDatePickers();
  }, { signal });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDatePickers();
  }, { signal });
}

function getInitialValues(fields) {
  return fields.reduce((values, field) => {
    values[field.key] = '';
    return values;
  }, {});
}

function groupFieldsBySection(fields) {
  return fields.reduce((groups, field) => {
    groups[field.section] = groups[field.section] || [];
    groups[field.section].push(field);
    return groups;
  }, {});
}

function validateForm(config, companySlug, equipmentTypeSlug, values, parts) {
  const errors = {};
  const company = config.company && config.company.slug === companySlug ? config.company : null;
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (!company) {
    errors.companySlug = 'Choose a valid company form.';
  }

  if (!config.equipmentType || config.equipmentType.slug !== equipmentTypeSlug) {
    errors.equipmentType = 'Choose a valid equipment type.';
  }

  for (const field of config.fields) {
    if (['partNumber', 'partQuantity'].includes(field.key)) {
      continue;
    }
    const value = String(values[field.key] || '').trim();

    if (field.required && !value) {
      errors[field.key] = `${field.label} is required.`;
      continue;
    }

    if (!value) {
      continue;
    }

    if (field.type === 'email' && !emailPattern.test(value)) {
      errors[field.key] = 'Enter a valid email address.';
    }

    if (field.type === 'number') {
      const numericValue = Number(value);
      if (!Number.isInteger(numericValue) || numericValue < (field.min || 0)) {
        errors[field.key] = `${field.label} must be a whole number of at least ${field.min || 0}.`;
      }
    }

    if (field.type === 'date' && !datePattern.test(value)) {
      errors[field.key] = 'Enter a valid date.';
    }
  }

  const partNumberField = config.fields.find((field) => field.key === 'partNumber');
  const quantityField = config.fields.find((field) => field.key === 'partQuantity');
  if (!Array.isArray(parts) || parts.length === 0) {
    errors.partNumber = 'Add at least one part.';
  } else {
    const missingPartIndex = partNumberField?.required
      ? parts.findIndex((part) => !String(part.partNumber || '').trim())
      : -1;
    const badQuantityIndex = parts.findIndex((part) => {
      const quantity = Number(String(part.quantity ?? '').trim());
      return !Number.isInteger(quantity) || quantity < (quantityField?.min || 1);
    });

    if (missingPartIndex >= 0) {
      errors.partNumber = `Enter a part number for part ${missingPartIndex + 1}.`;
    }
    if (badQuantityIndex >= 0) {
      errors.partQuantity = `Quantity for part ${badQuantityIndex + 1} must be a whole number of at least ${quantityField?.min || 1}.`;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}

async function submitOrder(
  app,
  config,
  company,
  equipmentType,
  values,
  parts,
  authClient,
  showCompanyPicker,
  photoAttachments
) {
  const submitButton = document.getElementById('submit-button');
  const clearButton = document.getElementById('clear-button');
  photoAttachments.setLocked(true);
  submitButton.disabled = true;
  clearButton.disabled = true;
  setStatus('Submitting form...');

  try {
    const accessToken = await authClient.getAccessToken();
    if (!accessToken) {
      window.location.replace(loginPath(equipmentTypePath(company.slug, equipmentType.slug)));
      return false;
    }

    let photoPayload = null;
    try {
      photoPayload = await photoAttachments.ensureUploaded(accessToken, (message) => setStatus(message));
    } catch (uploadError) {
      if (uploadError.status === 401) {
        await authClient.clearSession();
        window.location.replace(loginPath(equipmentTypePath(company.slug, equipmentType.slug)));
        return false;
      }
      setStatus(uploadError.message || 'A photo could not be uploaded. Your entries are still on screen.', 'error');
      return false;
    }

    const response = await fetch('/api/submissions', {
      method: 'POST',
      headers: {
        'X-WB-Parts-Access-Token': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        companySlug: company.slug,
        equipmentType: equipmentType.slug,
        fields: values,
        parts,
        ...(photoPayload || {})
      })
    });

  if (response.status === 401) {
    await authClient.clearSession();
    window.location.replace(loginPath(equipmentTypePath(company.slug, equipmentType.slug)));
    return false;
  }

    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.ok) {
      if (result.attachmentRetryRequired) {
        photoAttachments.restartUploads();
      }
      if (result.errors) {
        showFieldErrors(result.errors);
      }
      setStatus(result.message || 'The order could not be submitted. Please try again.', 'error');
      return false;
    }

    photoAttachments.clear();
    renderSuccess(
      app,
      config,
      company,
      equipmentType,
      result,
      authClient,
      showCompanyPicker
    );
    return true;
  } catch (error) {
    setStatus('Could not reach the submission service. Your entries are still on screen.', 'error');
    return false;
  } finally {
    photoAttachments.setLocked(false);
  }
}

function renderSuccess(
  app,
  config,
  company,
  equipmentType,
  result,
  authClient,
  showCompanyPicker
) {
  const equipmentSwitch = config.equipmentTypes.find((type) => type.slug !== equipmentType.slug);
  app.innerHTML = `
    ${renderTopbar(company, authClient.getIdentity(), showCompanyPicker, equipmentSwitch)}

    <section class="success-page" aria-labelledby="success-title">
      <span class="success-icon" aria-hidden="true">&#10003;</span>
      <h2 id="success-title">${escapeHtml(result.message || 'Order submitted successfully.')}</h2>
      <p>Your order has been received. You can safely close this page.</p>
      <button id="submit-another-button" class="secondary-button success-action" type="button">
        Submit Another Form
      </button>
    </section>
  `;

  document.getElementById('submit-another-button').addEventListener('click', async () => {
    await renderAuthorizedForm(app, config, company, equipmentType, authClient);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  bindSignOut(app, authClient);
}

let accountMenuAbortController = null;

function bindAccountMenu(app) {
  if (accountMenuAbortController) accountMenuAbortController.abort();
  accountMenuAbortController = new AbortController();
  const { signal } = accountMenuAbortController;
  const toggle = app.querySelector('[data-account-menu-toggle]');
  const menu = app.querySelector('[data-account-menu]');
  if (!toggle || !menu) return;

  function closeAccountMenu() {
    menu.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('click', (event) => {
    event.stopPropagation();
    const willOpen = menu.hidden;
    menu.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', String(willOpen));
  }, { signal });

  menu.addEventListener('click', (event) => event.stopPropagation(), { signal });
  document.addEventListener('click', closeAccountMenu, { signal });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAccountMenu();
  }, { signal });
}

function bindSignOut(app, authClient) {
  bindAccountMenu(app);
  const signOutButtons = Array.from(app.querySelectorAll('[data-sign-out]'));
  if (signOutButtons.length === 0) {
    return;
  }
  for (const signOutButton of signOutButtons) {
    signOutButton.addEventListener('click', async () => {
      for (const button of signOutButtons) {
        button.disabled = true;
      }
      await authClient.signOut();
    });
  }
}

function getEmailDomain(email) {
  const normalized = String(email || '').trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');
  return atIndex > 0 ? normalized.slice(atIndex + 1) : '';
}

function setStatus(message, type) {
  const panel = document.getElementById('status-panel');
  if (!panel) {
    return;
  }
  if (!message) {
    panel.className = 'form-status hidden';
    panel.textContent = '';
    return;
  }
  panel.className = `form-status ${type || ''}`.trim();
  panel.textContent = message;
}

function showFieldErrors(errors) {
  for (const [key, message] of Object.entries(errors)) {
    const inputs = getFieldErrorControls(key);
    const error = document.getElementById(`${key}-error`);
    for (const input of inputs) {
      input.setAttribute('aria-invalid', 'true');
    }
    if (error) {
      error.textContent = message;
    }
  }
}

function clearFieldError(key) {
  const inputs = getFieldErrorControls(key);
  const error = document.getElementById(`${key}-error`);
  for (const input of inputs) {
    input.removeAttribute('aria-invalid');
  }
  if (error) {
    error.textContent = '';
  }
}

function getFieldErrorControls(key) {
  if (key === 'partNumber') {
    return [...document.querySelectorAll('[data-part-number]')];
  }
  if (key === 'partQuantity') {
    return [...document.querySelectorAll('[data-part-quantity]')];
  }
  const control = document.getElementById(`${key}-display`) || document.getElementById(key);
  return control ? [control] : [];
}

function clearAllFieldErrors(fields) {
  for (const field of fields) {
    clearFieldError(field.key);
  }
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
